import { useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

interface LocationButtonProps {
  onLocationFetched: (location: string) => void;
  isDarkMode: boolean;
}

export const LocationButton = ({ onLocationFetched, isDarkMode }: LocationButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      toast({
        title: "Geolocation not supported",
        description: "Your browser doesn't support geolocation",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          // Try reverse geocoding to get a display name and stash coords for backend
          try {
            const resp = await fetch(
              `https://api.openweathermap.org/geo/1.0/reverse?lat=${latitude}&lon=${longitude}&limit=1&appid=ac4dd9820bbe9155e4a3fbbcd5921a65`
            );
            if (resp.ok) {
              const arr = await resp.json();
              const r0 = Array.isArray(arr) ? arr[0] : null;
              const place = r0?.name ? `${r0.name}${r0.state ? ', ' + r0.state : ''}${r0.country ? ', ' + r0.country : ''}` : null;
              // Stash for MessageHandler to use coordinates while message shows name
              try {
                localStorage.setItem('weather:lastCoords', JSON.stringify({ lat: latitude, lon: longitude }));
                if (place) localStorage.setItem('weather:lastPlace', place);
              } catch {}
              if (place) {
                onLocationFetched(place);
              } else {
                onLocationFetched(`${latitude},${longitude}`);
              }
            } else {
              // Fallback to coordinates string
              try { localStorage.setItem('weather:lastCoords', JSON.stringify({ lat: latitude, lon: longitude })); } catch {}
              onLocationFetched(`${latitude},${longitude}`);
            }
          } catch {
            try { localStorage.setItem('weather:lastCoords', JSON.stringify({ lat: latitude, lon: longitude })); } catch {}
            onLocationFetched(`${latitude},${longitude}`);
          }
        } catch (error) {
          console.error('Error getting location name:', error);
          onLocationFetched(`${position.coords.latitude},${position.coords.longitude}`);
        } finally {
          setIsLoading(false);
        }
      },
      (error) => {
        setIsLoading(false);
        toast({
          title: "Location access denied",
          description: "Please allow location access or enter a city name",
          variant: "destructive"
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  };

  return (
    <Button
      onClick={getCurrentLocation}
      disabled={isLoading}
      variant="outline"
      size="sm"
      className={`flex items-center gap-2 border-none rounded-b-none ${
        isDarkMode 
          ? 'bg-[#1e1e1e] text-white hover:bg-[#3c3c3c]' 
          : 'bg-white text-[#2e2e2e] hover:bg-gray-500/40 hover:text-[#2e2e2e]'
      }`}
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <MapPin className="w-4 h-4" />
      )}
      {isLoading ? 'Getting location...' : 'Use current location'}
    </Button>
  );
};
